// ============================================================================
// Component-scoped Comment attachment URL owner
// ============================================================================
//
// A CommentsSection mount is allowed to render private attachment thumbnails,
// but its presenters must not mint storage URLs themselves. This owner turns
// each visible attachment into a subject/role-scoped Query entry, dedupes the
// same object across comments and mounted records, and refreshes it before the
// 600-second URL expires. It deliberately is not a route resource: the finite
// comments-capable record mounts live in recordCollaborationRegistry instead
// of making a blanket claim about any of the 77 page surfaces.
// ============================================================================

import {useQueries} from '@tanstack/react-query';
import type {QueryKey as TanStackQueryKey, UseQueryOptions} from '@tanstack/react-query';
import {useMemo} from 'react';

import type {AuthState} from '../contexts/AuthContext.js';
import {getAttachmentSignedUrl, normalizeCommentAttachmentBucket} from '../lib/commentAttachments.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {resourceKey} from '../app/data/routeDataCatalog.js';
import type {QueryScope} from '../app/data/routeDataCatalog.js';
import {recordCollaborationScope} from './recordCollaborationData.js';
import {RECORD_COLLABORATION_ATTACHMENT_RESOURCE} from './recordCollaborationRegistry.js';

// This remains the server contract from commentAttachments. URLs are minted
// for ten minutes, never stretched into a longer browser-side cache.
export const COMMENT_ATTACHMENT_SIGNED_URL_TTL_SECONDS = 600;
export const COMMENT_ATTACHMENT_SIGNED_URL_TTL_MS = COMMENT_ATTACHMENT_SIGNED_URL_TTL_SECONDS * 1000;

// Query again thirty seconds before the server URL becomes unusable. The lead
// is intentional: the query must finish before expiry rather than treating a
// 403 from an already-expired <img> as the refresh signal.
export const COMMENT_ATTACHMENT_SIGNED_URL_REFRESH_LEAD_MS = 30 * 1000;
export const COMMENT_ATTACHMENT_SIGNED_URL_REFRESH_AFTER_MS =
  COMMENT_ATTACHMENT_SIGNED_URL_TTL_MS - COMMENT_ATTACHMENT_SIGNED_URL_REFRESH_LEAD_MS;
const MIN_REFRESH_DELAY_MS = 1_000;

export type CommentAttachmentUrlInput = {
  readonly path?: unknown;
  readonly bucket?: unknown;
};

export type CommentAttachmentUrlRow = {
  readonly deleted_at?: unknown;
  readonly attachments?: unknown;
};

export type NormalizedCommentAttachmentUrlInput = {
  readonly cacheKey: string;
  readonly path: string;
  readonly bucket: string;
};

export type CommentAttachmentSignedUrl = {
  readonly url: string;
  // `issuedAt` is captured BEFORE createSignedUrl begins. A slow request must
  // not make us pretend the URL was minted when its response finally arrived.
  readonly issuedAt: number;
  readonly expiresAt: number;
};

export type CommentAttachmentUrlProjection = {
  readonly url: string | null;
  readonly loading: boolean;
  readonly error: unknown;
};

export type CommentAttachmentUrlMap = Readonly<Record<string, CommentAttachmentUrlProjection>>;

const EMPTY_ATTACHMENT_URL: CommentAttachmentUrlProjection = Object.freeze({url: null, loading: false, error: null});

type CommentAttachmentUrlQueryOption = UseQueryOptions<
  CommentAttachmentSignedUrl,
  Error,
  CommentAttachmentSignedUrl,
  TanStackQueryKey
>;

type CommentAttachmentUrlQueryLike = {
  readonly data: CommentAttachmentSignedUrl | undefined;
  readonly isPending: boolean;
  readonly isFetching: boolean;
  readonly error: unknown;
};

/**
 * Normalize the untrusted jsonb metadata into both the allowlisted storage
 * selector and the exact cache identity. `processing-attachments:path` and a
 * fallback `comment-photos:path` can never collide, while an unknown bucket
 * resolves to the same default bucket the signing helper will actually read.
 */
export function normalizeCommentAttachmentUrlInput(
  attachment: CommentAttachmentUrlInput | null | undefined,
): NormalizedCommentAttachmentUrlInput | null {
  const path = typeof attachment?.path === 'string' ? attachment.path : '';
  if (!path) return null;
  const bucket = normalizeCommentAttachmentBucket(attachment?.bucket);
  return {cacheKey: `${bucket}:${path}`, path, bucket};
}

/**
 * The presenter only receives visible, non-deleted comment attachments. The
 * old render tree never mounted thumbnails in either deleted-comment branch;
 * retaining that filter avoids issuing new storage reads for hidden media.
 */
export function collectCommentAttachmentUrlInputs(
  comments: readonly CommentAttachmentUrlRow[],
): readonly NormalizedCommentAttachmentUrlInput[] {
  const byCacheKey = new Map<string, NormalizedCommentAttachmentUrlInput>();
  for (const comment of comments) {
    if (comment.deleted_at || !Array.isArray(comment.attachments)) continue;
    for (const attachment of comment.attachments) {
      const normalized = normalizeCommentAttachmentUrlInput(
        attachment && typeof attachment === 'object' ? (attachment as CommentAttachmentUrlInput) : undefined,
      );
      if (normalized) byCacheKey.set(normalized.cacheKey, normalized);
    }
  }
  return [...byCacheKey.values()];
}

function throwIfAborted(signal: AbortSignal): void {
  // Storage's createSignedUrl API has no AbortSignal parameter. Reading the
  // Query signal still lets Query retire this observation on unmount/key
  // change; the post-await check prevents that abandoned result from becoming
  // a usable URL in its old cache entry.
  if (signal.aborted) throw new Error('Comment attachment signing was aborted.');
}

/** The one physical signing call for every mounted comment thumbnail. */
export async function loadCommentAttachmentSignedUrl(
  sb: SupabaseClient,
  attachment: NormalizedCommentAttachmentUrlInput,
  signal: AbortSignal,
  now: () => number = Date.now,
): Promise<CommentAttachmentSignedUrl> {
  throwIfAborted(signal);
  const issuedAt = now();
  const url = await getAttachmentSignedUrl(
    sb,
    attachment.path,
    COMMENT_ATTACHMENT_SIGNED_URL_TTL_SECONDS,
    attachment.bucket,
  );
  throwIfAborted(signal);
  // A null from the legacy helper means storage rejected the request. Preserve
  // its visual fallback, but make the failed Query observable so it is not
  // cached as a valid blank URL for the rest of this mount.
  if (!url) throw new Error('Could not load comment attachment.');
  return {url, issuedAt, expiresAt: issuedAt + COMMENT_ATTACHMENT_SIGNED_URL_TTL_MS};
}

/**
 * Time until the next refresh. A query result carries the issuance timestamp
 * instead of trusting Query's `dataUpdatedAt`, because a slow signing request
 * can otherwise schedule its next mint after the URL has already expired.
 */
export function commentAttachmentRefreshDelay(
  data: CommentAttachmentSignedUrl | undefined,
  now: number = Date.now(),
): number | false {
  if (!data || !data.url || !Number.isFinite(data.expiresAt)) return false;
  return Math.max(MIN_REFRESH_DELAY_MS, data.expiresAt - now - COMMENT_ATTACHMENT_SIGNED_URL_REFRESH_LEAD_MS);
}

/** Project a Query state without exposing an expired signed URL to the DOM. */
export function commentAttachmentUrlProjection(
  query: CommentAttachmentUrlQueryLike,
  now: number = Date.now(),
): CommentAttachmentUrlProjection {
  const data = query.data;
  const url = data && data.expiresAt > now ? data.url : null;
  return {
    url,
    loading: Boolean(!url && (query.isPending || query.isFetching)),
    error: query.error ?? null,
  };
}

/** Return the presenter's safe fallback when a row has no signable path. */
export function commentAttachmentUrlFor(
  urls: CommentAttachmentUrlMap,
  attachment: CommentAttachmentUrlInput | null | undefined,
): CommentAttachmentUrlProjection {
  const normalized = normalizeCommentAttachmentUrlInput(attachment);
  return normalized ? urls[normalized.cacheKey] || EMPTY_ATTACHMENT_URL : EMPTY_ATTACHMENT_URL;
}

function commentAttachmentQueryKey(scope: QueryScope, attachment: NormalizedCommentAttachmentUrlInput) {
  return resourceKey(RECORD_COLLABORATION_ATTACHMENT_RESOURCE.SIGNED_URL, scope, {
    bucket: attachment.bucket,
    path: attachment.path,
  });
}

/**
 * Component-scoped owner for every automatic attachment read made by one
 * CommentsSection. It is intentionally the only useQueries owner; individual
 * CommentAttachmentThumb instances are now pure loading/fallback presenters.
 */
export function useRecordCollaborationCommentAttachmentUrls({
  sb,
  authState,
  comments,
}: {
  sb: SupabaseClient;
  authState: AuthState;
  comments: readonly CommentAttachmentUrlRow[];
}): CommentAttachmentUrlMap {
  const scope = useMemo(() => recordCollaborationScope(authState), [authState]);
  const attachments = useMemo(() => collectCommentAttachmentUrlInputs(comments), [comments]);
  const queryOptions = useMemo<readonly CommentAttachmentUrlQueryOption[]>(() => {
    // A signed URL is private. There is no public/signed-out variant and no
    // inert shared key: without an authenticated collaboration scope, mount no
    // query observers at all.
    if (!scope) return [];
    return attachments.map((attachment) => ({
      queryKey: commentAttachmentQueryKey(scope, attachment),
      // Destructuring signal is load-bearing. Query only retires an orphaned
      // request when the function reads it; loadCommentAttachmentSignedUrl
      // checks before and after the SDK call, whose API lacks signal support.
      queryFn: ({signal}: {signal: AbortSignal}) => loadCommentAttachmentSignedUrl(sb, attachment, signal),
      // This narrow exception to route-data defaults is the URL's server TTL,
      // not a freshness policy for farm data. A duplicate thumbnail shares the
      // still-valid URL, then all observers refresh thirty seconds before it
      // expires.
      staleTime: COMMENT_ATTACHMENT_SIGNED_URL_REFRESH_AFTER_MS,
      refetchInterval: (query: {state: {data: CommentAttachmentSignedUrl | undefined}}) =>
        commentAttachmentRefreshDelay(query.state.data),
      // Background tabs do not need to mint private URLs continuously. On
      // return, this opt-in fetches only after the TTL-before-expiry stale
      // boundary, so a foregrounded thumbnail does not keep a dead href.
      refetchOnWindowFocus: true,
    }));
  }, [attachments, sb, scope]);
  const queries = useQueries({queries: queryOptions});

  return useMemo(() => {
    const urls: Record<string, CommentAttachmentUrlProjection> = {};
    for (let index = 0; index < attachments.length; index += 1) {
      urls[attachments[index].cacheKey] = commentAttachmentUrlProjection(queries[index]);
    }
    return urls;
  }, [attachments, queries]);
}
